import React, { useState, useEffect, useRef } from 'react';
import { X, SkipForward, Play, Pause } from 'lucide-react';

/**
 * BreakExercises — animated kawaii critters demonstrating break movements.
 * Triggered when a work-focus session ends.
 */

const EXERCISES = [
  {
    id: 'neck_rolls',
    emoji: '🦊',
    name: 'Nackrullningar',
    instruction: 'Rulla huvudet långsamt i cirklar, 4 gånger åt varje håll. Känn spänningarna släppa.',
    duration: 30,
    animation: 'spin-slow',
    tip: 'Stäng ögonen om du vill — låt det bara kännas.',
  },
  {
    id: 'shoulder_roll',
    emoji: '🐻',
    name: 'Axelrullningar',
    instruction: 'Lyft axlarna upp mot öronen, rulla bakåt och ner. 5 gånger bakåt, 5 gånger framåt.',
    duration: 30,
    animation: 'shrug',
    tip: 'Extra skönt att göra med djupa andetag.',
  },
  {
    id: 'reach_up',
    emoji: '🐰',
    name: 'Sträck armar mot taket',
    instruction: 'Flätat fingrarna, tryck handflatorna mot taket, andas in djupt. Håll i 10 sek, släpp. Upprepa 3 gånger.',
    duration: 40,
    animation: 'reach',
    tip: 'Tänk att du är en blomma som sträcker sig efter solen.',
  },
  {
    id: 'side_bend',
    emoji: '🐱',
    name: 'Sträck sidorna',
    instruction: 'Lyft höger arm, luta dig mjukt åt vänster. Håll 10 sek. Byt sida. Upprepa.',
    duration: 40,
    animation: 'sway',
    tip: 'Det ska kännas gott, inte tvingande — så långt din kropp vill.',
  },
  {
    id: 'eye_rest',
    emoji: '🦉',
    name: 'Vila ögonen',
    instruction: 'Titta bort från skärmen. Fokusera på något 6 meter bort i 20 sekunder. Blinka några gånger.',
    duration: 20,
    animation: 'blink',
    tip: 'Detta är 20-20-20-regeln. Dina ögon kommer tacka dig.',
  },
  {
    id: 'stand_walk',
    emoji: '🐢',
    name: 'Gå en liten runda',
    instruction: 'Res dig upp. Gå runt lite i rummet. Kanske hämta vatten. Rör på dig i 1 minut.',
    duration: 60,
    animation: 'walk',
    tip: 'Din kropp är gjord för att röra sig, inte sitta still.',
  },
  {
    id: 'breathing',
    emoji: '🐼',
    name: 'Andning',
    instruction: 'Andas in genom näsan 4 sek. Håll 4 sek. Andas ut genom munnen 6 sek. Upprepa 4 gånger.',
    duration: 60,
    animation: 'breathe',
    tip: 'Lång utandning lugnar nervsystemet direkt.',
  },
  {
    id: 'water',
    emoji: '🦊',
    name: 'Drick vatten',
    instruction: 'Ta minst 2-3 klunkar vatten. Ditt fokus tappar kraft när du är torr.',
    duration: 15,
    animation: 'drink',
    tip: 'Hjärnan är ~75% vatten. Ge den det den behöver.',
  },
];

function pickBreakSet() {
  // Always start with eye rest, then 3 movement exercises, end with water
  const movement = [
    'neck_rolls', 'shoulder_roll', 'reach_up', 'side_bend', 'breathing', 'stand_walk',
  ];
  const picked = [...movement].sort(() => Math.random() - 0.5).slice(0, 3);
  return [
    EXERCISES.find((e) => e.id === 'eye_rest'),
    ...picked.map((id) => EXERCISES.find((e) => e.id === id)),
    EXERCISES.find((e) => e.id === 'water'),
  ];
}

export default function BreakExercises({ onClose }) {
  const [list] = useState(pickBreakSet);
  const [idx, setIdx] = useState(0);
  const [remaining, setRemaining] = useState(list[0]?.duration || 30);
  const [paused, setPaused] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    setRemaining(list[idx]?.duration || 30);
  }, [idx, list]);

  useEffect(() => {
    if (paused) return;
    intervalRef.current = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) {
          if (idx < list.length - 1) {
            setIdx((i) => i + 1);
            return list[idx + 1]?.duration || 30;
          } else {
            clearInterval(intervalRef.current);
            return 0;
          }
        }
        return r - 1;
      });
    }, 1000);
    return () => clearInterval(intervalRef.current);
  }, [idx, paused, list]);

  const cur = list[idx];
  const isLast = idx === list.length - 1 && remaining === 0;

  return (
    <div
      className="fixed inset-0 z-[10000] bg-gradient-to-br from-[#E8F5E8] via-[#FFE8F0] to-[#E8F0FF] flex items-center justify-center p-4 overflow-y-auto"
      data-testid="break-exercises"
    >
      <div className="max-w-md w-full">
        <div className="neo-card p-6 bg-white relative">
          <button
            onClick={onClose}
            className="absolute top-2 right-2 neo-btn p-1.5 bg-white"
            aria-label="Stäng"
            data-testid="break-close"
          >
            <X className="w-4 h-4" />
          </button>

          <p className="text-center text-xs font-bold opacity-60 mb-1">
            Pausövning {idx + 1} / {list.length}
          </p>
          <h2 className="text-center text-2xl font-bold font-['Fredoka']" data-testid="break-exercise-name">
            {cur?.name}
          </h2>

          {/* Animated pet */}
          <div className="flex items-center justify-center my-6" aria-hidden>
            <div
              className="text-[100px] leading-none"
              style={{ animation: `${cur?.animation || 'bobup'} 3s ease-in-out infinite` }}
              data-testid="break-exercise-pet"
            >
              {cur?.emoji}
            </div>
          </div>

          <p className="text-center text-sm leading-relaxed" data-testid="break-exercise-instruction">
            {cur?.instruction}
          </p>

          {cur?.tip && (
            <p className="text-center text-xs mt-3 italic text-black/60">💡 {cur.tip}</p>
          )}

          {/* Timer */}
          <div className="mt-5 flex items-center justify-center gap-3">
            <button
              onClick={() => setPaused((p) => !p)}
              className="neo-btn p-2 bg-white"
              aria-label={paused ? 'Fortsätt' : 'Paus'}
              data-testid="break-pause"
            >
              {paused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
            </button>
            <div className="text-4xl font-bold font-['Fredoka']" data-testid="break-exercise-countdown">
              {remaining}s
            </div>
            <button
              onClick={() => {
                if (idx < list.length - 1) setIdx((i) => i + 1);
                else onClose();
              }}
              className="neo-btn p-2 bg-white"
              aria-label="Nästa"
              data-testid="break-next"
            >
              <SkipForward className="w-4 h-4" />
            </button>
          </div>

          {/* Progress */}
          <div className="mt-4 h-2 bg-black/10 rounded-full overflow-hidden border border-black">
            <div
              className="h-full bg-gradient-to-r from-[#B9FBC0] to-[#A2D2FF] transition-all"
              style={{ width: `${cur?.duration ? ((cur.duration - remaining) / cur.duration) * 100 : 0}%` }}
            />
          </div>

          {isLast && (
            <button
              onClick={onClose}
              className="mt-5 neo-btn w-full py-3 bg-[#B9FBC0] font-bold"
              data-testid="break-done"
            >
              ✨ Klar — dags att fokusera igen
            </button>
          )}
        </div>
      </div>

      <style>{`
        @keyframes bobup {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-16px); }
        }
        @keyframes spin-slow {
          0%, 100% { transform: rotate(0deg); }
          25% { transform: rotate(-15deg); }
          50% { transform: rotate(0deg); }
          75% { transform: rotate(15deg); }
        }
        @keyframes shrug {
          0%, 100% { transform: translateY(0) scaleY(1); }
          50% { transform: translateY(-10px) scaleY(1.1); }
        }
        @keyframes reach {
          0%, 100% { transform: translateY(0) scale(1); }
          50% { transform: translateY(-20px) scale(1.1); }
        }
        @keyframes sway {
          0%, 100% { transform: translateX(0) rotate(0deg); }
          25% { transform: translateX(-15px) rotate(-10deg); }
          75% { transform: translateX(15px) rotate(10deg); }
        }
        @keyframes blink {
          0%, 90%, 100% { transform: scaleY(1); }
          95% { transform: scaleY(0.1); }
        }
        @keyframes walk {
          0%, 100% { transform: translateX(-20px); }
          50% { transform: translateX(20px); }
        }
        @keyframes breathe {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.25); }
        }
        @keyframes drink {
          0%, 100% { transform: rotate(0deg); }
          50% { transform: rotate(-20deg); }
        }
      `}</style>
    </div>
  );
}
